import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
import type { Ref } from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';
import { ProModal, AccountModal } from '@/lazy-load-components';
import { ImpressionTrackingEvents } from '@/enums/events';
import { AdProvider, loadAd, removeAd } from '@/helpers/ad/ad-helper';
import { SNIGEL_AUCTION_TITLE_KEY } from '@/helpers/ad/snigel-helper';
import type { UrlMetadata } from '@/stores/pinia/useRoutingStore';
import type { TitleDetailsFragment } from '@/pages/graphql/fragments/TitleDetails.fragment';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { saveLastProView } from '@/helpers/pro-web-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { lowPriorityCallback } from '@/helpers/request-idle-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { storeToRefs } from 'pinia';
import { useExperiment } from '@/libraries/experiment';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { injectLocal, until } from '@vueuse/core';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'SnigelExpAdUnit',
  props: {
    adPosition: null,
    subgenres: { default: undefined },
    additionalContexts: { default: () => [] },
    allowAdRemoval: { type: Boolean, default: true },
    titleId: null,
    isModal: { type: Boolean }
  },
  emits: ["adClicked", "removeAdClicked"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'adClicked'): void;
	(e: 'removeAdClicked'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { adPosition: string, subgenres: TitleDetailsFragment['content']['subgenres'], additionalContexts: SnowplowContext[], allowAdRemoval: boolean, titleId?: string, isModal?: boolean };

Vue.use(VueObserveVisibility);





const { webLocale, isProWebLocale } = storeToRefs(useLanguageStore());
const { activeRoute, urlMetadataPromise } = storeToRefs(useRoutingStore());
const { jwLoginId, strictlyAllConsentsGranted } = storeToRefs(useUserStore());
const { overridePageState } = storeToRefs(useTrackingStore());

const isShown = ref(false);
const adUnit = ref<HTMLElement | null>(null);
const showSnigelOnModal = computed(() => props.isModal && overridePageState.value?.pageEntryMode === 'modal');

const { activeVariantWithControlGroup: variant } = useExperiment('EXP_SNIGEL');

const isSnigel = computed(() => variant.value === 'variant_1');
const isYieldlove = computed(() => variant.value === 'control');

const adProvider = computed<AdProvider | null>(() => {
	if (isSnigel.value) return AdProvider.SNIGEL;
	if (isYieldlove.value) return AdProvider.YIELDLOVE;
	return null;
});

// Snigel uses its own adngin- prefix; Yieldlove reuses the normal prod slot so experiment_name targeting differentiates reporting
const yieldLoveSlotId = computed(() => `jw_${webLocale.value}_${props.adPosition}`);
const snigelSlotId = computed(() => `adngin-${props.adPosition}-${showSnigelOnModal.value ? '1' : '0'}`);

const slotId = computed(() => {
	if (isYieldlove.value) return yieldLoveSlotId.value;
	if (isSnigel.value) return snigelSlotId.value;
	return '';
});

// EXP_SNIGEL: identifies the current title so startSnigelAuction() runs once per title. The ref itself
// is shared across every SnigelExpAdUnit instance on the page (provided once by TitleDetailV1.vue), so
// whichever ad slot becomes visible first is the only one that actually starts the auction - the other
// slots on the same page skip straight to isShown without calling loadAd again.
const lastAuctionTitleId = injectLocal<Ref<string | null>>(SNIGEL_AUCTION_TITLE_KEY, ref<string | null>(null));

const targeting = computed(() => {
	if (isSnigel.value) return null;

	const result: Record<string, string> = {};

	if (isYieldlove.value) {
		result.experiment_name = 'EXP_SNIGEL_CONTROL';
	}

	const subgenreShortNames = props.subgenres?.map(s => s.content.shortName).filter(s => s != null) ?? [];
	if (subgenreShortNames.length) result.subgenre = subgenreShortNames.join(',');

	return Object.keys(result).length ? result : null;
});

const onVisibleCallback = {
	callback: (isVisible: boolean, entry: IntersectionObserverEntry) => onAdImpression(isVisible, entry),
};

async function setShown(_urlData: UrlMetadata) {
	const provider = adProvider.value;
	if (!provider || isShown.value) return;

	if (process.client) {
		if (hasUCConsentBanner(webLocale.value)) await until(strictlyAllConsentsGranted).toBeTruthy();

		const shouldLoadAd = !isSnigel.value || lastAuctionTitleId.value !== props.titleId;
		if (shouldLoadAd) {
			if (isSnigel.value) lastAuctionTitleId.value = props.titleId ?? '';
			await loadAd(webLocale.value, slotId.value, provider, undefined, targeting.value);
		}
	}

	isShown.value = true;
}

function onAdImpression(isVisible: boolean, _entry: any) {
	if (!isVisible) return;
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.AD,
		{ action: 'impression', label: 'banner', nonInteraction: true },
		props.additionalContexts
	);
}

function onAdClick() {
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.AD,
		{ action: 'click', label: 'banner' },
		props.additionalContexts
	);
	emits('adClicked');
}

function onMouseLeave() {
	if (adUnit.value?.querySelector('iframe') === document.activeElement) {
		window.focus();
	}
}

function triggerAdClickOnBlur() {
	if (adUnit.value?.querySelector('iframe') === document.activeElement) {
		onAdClick();
	}
}

function openModal() {
	const location = 'RemoveAds';
	if (isProWebLocale.value) {
		ModalHelper.openModal(ProModal, {}, {}, location, true);
		saveLastProView(location);
	} else {
		ModalHelper.openModal(AccountModal, { closable: true }, { cssClass: 'account-modal' }, 'AdUnit');
	}
	emits('removeAdClicked');
}

watch(
	() => [activeRoute.value, jwLoginId.value],
	async () => {
		const urlData = await urlMetadataPromise.value!;
		await setShown(urlData);
	}
);

onMounted(async () => {
	if (!adProvider.value) return;
	const urlData = await urlMetadataPromise.value!;
	lowPriorityCallback(() => setShown(urlData));
	window.addEventListener('blur', triggerAdClickOnBlur);
});

onBeforeUnmount(() => {
	window.removeEventListener('blur', triggerAdClickOnBlur);
});

onUnmounted(async () => {
	if (hasUCConsentBanner(webLocale.value)) await until(strictlyAllConsentsGranted).toBeTruthy();
	if (adProvider.value) removeAd(webLocale.value, slotId.value, adProvider.value);
});

return { __sfc: true,props, emits, webLocale, isProWebLocale, activeRoute, urlMetadataPromise, jwLoginId, strictlyAllConsentsGranted, overridePageState, isShown, adUnit, showSnigelOnModal, variant, isSnigel, isYieldlove, adProvider, yieldLoveSlotId, snigelSlotId, slotId, lastAuctionTitleId, targeting, onVisibleCallback, setShown, onAdImpression, onAdClick, onMouseLeave, triggerAdClickOnBlur, openModal, faTimes }
}

})